import { useTheme, Button, Box, Grid, Typography, CircularProgress } from '@mui/material';
import { GridRowsProp } from '@mui/x-data-grid';
import { AxiosResponse } from 'axios';
import Mui from 'materials';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import Breadcrumbs from 'components/breadcrumbs';
import Error from 'components/error';
import Paper from 'components/paper';

import TextField from 'components/text-field';
import ToggleButton from 'components/toggle-button';
import LoadingSpinner from 'components/loading-spinner';
import ProfilesStrategiesTabHeader from '../../../components/profile-strategies-tabs-header';

import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { OwnerType } from 'types/shared/owner-type.enum';
import { PeriodizationType } from 'views/profiles-strategies/features/profiles/types/periodization-type.enum';
import { ProfileSubType, ProfileType } from 'types/shared/profile-type.enum';
import { UpdateProfileInput } from 'views/profiles-strategies/features/profiles/types/update-profile-input.type';

import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';

import EditProfilesGrid from 'views/profiles-strategies/components/profiles-data-grid';
import { getProfileByType } from '../api/get-profile';
import { putProfileByType } from '../api/update-profile';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';

type ParamTypes = {
  profileType: ProfileType;
  profileId: string;
};

type Row = {
  id: string;
  period: number;
  average: number;
  rowIndex: number;
};

type Props = {};

const PAGE_NAME_ID = 'profilesStrategies.editProfiles';

const EditProfiles: React.FC<Props> = () => {
  const intl = useIntl();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();
  const [rows, setRows] = useState<GridRowsProp<Row>>([]);
  const { errorDictionary, changeErrorDictionary, handleEditing, isEditing, hasError } = useDataGridError();

  // FORMS
  const { handleSubmit, control, reset } = useForm<UpdateProfileInput>({
    defaultValues: {
      name: '',
      active: false,
      circular: false,
      fromInput: false,
      isTemporary: false,
      modelSubType: ProfileSubType.EGI,
      periodisation: PeriodizationType.WEEK,
      lines: [{ period: 1, average: 0 }],
    },
    shouldUnregister: false,
  });

  // FETCH DATA
  const {
    data: profile,
    error,
    isError,
    isLoading,
  } = useGet(
    ['getProfileByType', params.profileType, params.profileId],
    (context) => getProfileByType(context.queryKey[1] as ProfileType, String(context.queryKey[2])),
    {
      enabled: !!params.profileType && !!params.profileId,
    }
  );

  useEffect(() => {
    if (!profile) return;

    reset({
      name: profile.name,
      active: profile.active,
      circular: profile.circular,
      fromInput: profile.fromInput,
      isTemporary: profile.isTemporary,
      modelSubType: profile.modelSubType,
      periodisation: profile.periodisation,
    });
  }, [profile, reset]);

  // POST DATA
  const updateProfile = usePost<AxiosResponse, UpdateProfileInput>(
    (request) => putProfileByType(profile!.modelType, String(profile!.id), request),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getProfileByType', params.profileType, params.profileId],
        exact: true
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // HANDLERS
  const canManageProfileStrategy = (owner: OwnerType | undefined): boolean => {
    switch (owner) {
      case OwnerType.MARKET:
        return account.company?.features.hasPlanningPrognosisManageMarketProfiles ?? false;
      case OwnerType.COMPANY:
      case OwnerType.FISH_GROUP:
      case OwnerType.SITE:
        return account.company?.features.hasPlanningPrognosisManageCompanyOrSiteProfiles ?? false;
      default:
        return false;
    }
  };

  const handleUpdateProfile = (request: UpdateProfileInput) => {
    let data = {
      ...request,
      lines: rows.map((line) => {
        return {
          period: line.period,
          average: line.average,
        };
      }),
    };
    updateProfile.mutate(data);
  };

  return isError ? (
    <Error error={error} />
  ) : !profile || isLoading ? (
    <LoadingSpinner />
  ) : (
    <>
      <ProfilesStrategiesTabHeader
        utils={
          <Breadcrumbs
            crumbs={[
              {
                text: profile.name,
                url: profile.ownerSiteId,
              },
            ]}
          />
        }
      />

      <form onSubmit={handleSubmit(handleUpdateProfile)}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper transparent>
              <Typography pb={4} variant="h3" color={theme.palette.common.white}>
                <FormattedMessage id={`profilesStrategies.generalTitle`} />
              </Typography>
              <Paper>
                <TextField
                  dark
                  fullWidth
                  label={intl.formatMessage({ id: `profilesStrategies.label.name` })}
                  disabled={!canManageProfileStrategy(profile.ownerType)}
                  controller={{
                    name: 'name',
                    control: control,
                    rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                  }}
                />
                <Grid>
                  <ToggleButton
                    dark
                    margin="dense"
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.profileToggle.active` })}
                    disabled={!canManageProfileStrategy(profile.ownerType)}
                    controller={{ name: 'active', control: control }}
                  />
                  <ToggleButton
                    dark
                    margin="dense"
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.profileToggle.temporary` })}
                    disabled={!canManageProfileStrategy(profile.ownerType)}
                    controller={{ name: 'isTemporary', control: control }}
                  />
                  <ToggleButton
                    dark
                    margin="dense"
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.profileToggle.circular` })}
                    disabled={!canManageProfileStrategy(profile.ownerType)}
                    controller={{ name: 'circular', control: control }}
                  />
                  <ToggleButton
                    dark
                    margin="dense"
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.profileToggle.input` })}
                    disabled={!canManageProfileStrategy(profile.ownerType)}
                    controller={{ name: 'fromInput', control: control }}
                  />
                </Grid>
              </Paper>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper transparent>
              <Typography pb={4} variant="h3" color={theme.palette.common.white}>
                <FormattedMessage id={`profilesStrategies.ownerTitle`} />
              </Typography>
              <Paper>
                <Mui.FormControl fullWidth margin="dense">
                  <Typography variant="caption">
                    <FormattedMessage id={`profilesStrategies.menuItem.market`} />
                  </Typography>
                  <Typography>{profile.ownerMarketId}</Typography>
                </Mui.FormControl>
                {profile.ownerType === OwnerType.COMPANY && (
                  <Mui.FormControl fullWidth margin="dense">
                    <Typography variant="caption">
                      <FormattedMessage id={`profilesStrategies.menuItem.company`} />
                    </Typography>
                    <Typography>{profile.ownerCompanyId}</Typography>
                  </Mui.FormControl>
                )}
                {profile.ownerType === OwnerType.SITE && (
                  <Mui.FormControl fullWidth margin="dense">
                    <Typography variant="caption">
                      <FormattedMessage id={`profilesStrategies.menuItem.site`} />
                    </Typography>
                    <Typography>{profile.ownerSiteId}</Typography>
                  </Mui.FormControl>
                )}
                <Mui.FormControl fullWidth margin="dense">
                  <Typography variant="caption">
                    <FormattedMessage id={`profilesStrategies.menuItem.connectedFishGroups`} />
                  </Typography>
                  <Typography>{profile.numberOfConnectedFishgroups}</Typography>
                </Mui.FormControl>
              </Paper>
            </Paper>
          </Grid>
          <Grid size={12}>
            <Paper transparent>
              <Typography pb={4} variant="h3" color={theme.palette.common.white}>
                <FormattedMessage id={`profilesStrategies.linesTitle`} />
              </Typography>
              <Box>
                <EditProfilesGrid
                  rows={rows}
                  setRows={setRows}
                  isLoading={isLoading}
                  profile={profile.lines}
                  handleEditing={handleEditing}
                  errorDictionary={errorDictionary}
                  changeErrorDictionary={changeErrorDictionary}
                  isDisabled={!canManageProfileStrategy(profile?.ownerType)}
                />
              </Box>
              <Box display="flex" justifyContent="flex-end" mt={4}>
                <Button type="submit" disabled={updateProfile.isPending || isEditing || hasError}>
                  {updateProfile.isPending ? (
                    <CircularProgress color="inherit" />
                  ) : (
                    <FormattedMessage id="general.submit" values={{ type: '' }} />
                  )}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default EditProfiles;
